import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import clientFeedbackImg from '../../assets/images/client_feeback_img.jpg';
import { Container, Eyebrow, easedGradient, NAVY, LIGHT } from './ui';

const stories = [
  {
    name: 'Rinku Biswas',
    role: 'Co-Founder, RB Travels and Tour',
    text: 'Working with Binud has been an exceptional experience. He developed our website, set up business analytics tools, and managed our digital marketing - his technical expertise gave us a strong online presence.',
  },
  {
    name: 'Amit Sharma',
    role: 'Startup Founder, TechLaunch India',
    text: 'Binud delivered our MVP in record time. Clean code, great communication, and he genuinely understood our vision. Highly recommended for any startup looking to move fast.',
  },
  {
    name: 'Priya Nath',
    role: 'Business Owner, Nath Enterprises',
    text: 'Our e-commerce platform was built from scratch by Binud. The attention to detail and post-launch support has been outstanding, and our sales improved significantly after launch.',
  },
  {
    name: 'Rahul Dev',
    role: 'Product Manager, SoftEdge Solutions',
    text: 'Binud is a reliable technology partner. He built our internal dashboard and automation tools that save our team hours every week. Truly a problem solver.',
  },
];

const RoundButton = ({ onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="w-11 h-11 rounded-full bg-[#111] text-white flex items-center justify-center transition-transform hover:-translate-y-0.5"
  >
    {children}
  </button>
);

const ClientStories = () => {
  const [index, setIndex] = useState(0);
  const go = (step) => setIndex((i) => (i + step + stories.length) % stories.length);
  const story = stories[index];

  return (
    <section id="testimonials" className="relative">
      <div className="bg-[#0b2471] pt-4">
        <Container>
          <div>
            <Eyebrow dark>Client Feedback</Eyebrow>
            <h2 className="mt-3 text-[32px] sm:text-[42px] lg:text-[46px] font-medium leading-[1.08] tracking-[-0.04em] text-white">
              Trusted by ambitious
              <br />
              teams and founders
            </h2>
          </div>
        </Container>
      </div>

      {/* Card straddles the navy → light transition */}
      <div className="pt-12 pb-16" style={{ background: easedGradient(NAVY, LIGHT, 30, 100) }}>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 rounded-3xl bg-[#f4f6fa] p-3 sm:p-4 shadow-[0_30px_80px_rgba(11,36,113,0.25)]">
            <div className="flex flex-col justify-between px-3 sm:px-10 pt-8 pb-4 min-h-[360px]">
              <div className="flex gap-6 sm:gap-8 my-auto">
                <div className="flex flex-col gap-1.5 pt-[60px]">
                  {stories.map((s, i) => (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => setIndex(i)}
                      aria-label={`Show story ${i + 1}`}
                      className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center transition-colors ${
                        i === index ? 'bg-[#111] text-white' : 'border border-[#111]/40 text-[#111]/60 hover:border-[#111]'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <div className="flex-1">
                  <span className="inline-block bg-[#111] text-white text-xs rounded-full px-4 py-2">Customer Stories</span>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.35 }}
                    >
                      <p className="mt-6 text-[15px] sm:text-base leading-relaxed text-[#111] max-w-[340px]">{story.text}</p>
                      <p className="mt-6 text-[15px] font-medium text-[#111]">{story.name}</p>
                      <p className="mt-1 text-sm text-[#111]/60">{story.role}</p>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
              <div className="flex justify-center gap-2 pt-8">
                <RoundButton onClick={() => go(-1)} label="Previous story">
                  <ArrowLeft className="w-4 h-4" />
                </RoundButton>
                <RoundButton onClick={() => go(1)} label="Next story">
                  <ArrowRight className="w-4 h-4" />
                </RoundButton>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden h-[280px] sm:h-[360px] lg:h-auto lg:min-h-[440px]">
              <img
                src={clientFeedbackImg}
                alt="Code editor open on a laptop under blue light"
                loading="lazy"
                className="w-full h-full object-cover object-[60%_50%]"
              />
            </div>
          </div>
        </Container>
      </div>
    </section>
  );
};

export default ClientStories;
